import { useEffect, useMemo, useState } from "react";

const getStandalone = () => {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(display-mode: standalone)")?.matches) return true;
    if (window.navigator?.standalone) return true;
  } catch (_) {
    return false;
  }
  return false;
};

const getIosSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return isIOS && isSafari;
};

const getCapturedPrompt = () => {
  if (typeof window === "undefined") return null;
  return window.__MD_PWA_DEFERRED_PROMPT__ || null;
};

export const usePwaInstall = () => {
  const [promptEvent, setPromptEvent] = useState(() => getCapturedPrompt());
  const [installed, setInstalled] = useState(() => getStandalone());
  const [installing, setInstalling] = useState(false);
  const [busy, setBusy] = useState(false);
  const isIosSafari = useMemo(() => getIosSafari(), []);

  useEffect(() => {
    const register = async () => {
      if (!("serviceWorker" in navigator)) return;
      try {
        const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
        registration.update?.();
      } catch (_) {
        /* service worker is optional */
      }
    };

    const applyCapturedPrompt = () => {
      const captured = getCapturedPrompt();
      if (captured) setPromptEvent(captured);
      setInstalled(getStandalone());
    };

    const onBeforeInstallPrompt = (event) => {
      try {
        event.preventDefault();
      } catch (_) {
        /* ignore */
      }
      window.__MD_PWA_DEFERRED_PROMPT__ = event;
      setPromptEvent(event);
      setInstalling(false);
      setInstalled(getStandalone());
    };

    const onInstalled = () => {
      window.__MD_PWA_DEFERRED_PROMPT__ = null;
      setInstalling(false);
      setInstalled(true);
      setPromptEvent(null);
    };

    applyCapturedPrompt();
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("morfeusz:pwa-install-ready", applyCapturedPrompt);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("morfeusz:pwa-installed", onInstalled);
    window.addEventListener("load", register, { once: true });
    register();

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("morfeusz:pwa-install-ready", applyCapturedPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("morfeusz:pwa-installed", onInstalled);
      window.removeEventListener("load", register);
    };
  }, []);

  const install = async () => {
    if (installed || busy) return "idle";
    if (isIosSafari) return "ios";

    const prompt = promptEvent || getCapturedPrompt();
    if (!prompt) return "unavailable";

    setBusy(true);
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      window.__MD_PWA_DEFERRED_PROMPT__ = null;
      setPromptEvent(null);
      if (choice?.outcome === "accepted") {
        setInstalling(true);
        return "accepted";
      }
      setInstalling(false);
      return "dismissed";
    } catch (_) {
      setInstalling(false);
      return "error";
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    canInstall: Boolean(promptEvent || getCapturedPrompt()) || isIosSafari,
    installed,
    installing,
    isIosSafari,
    install,
  };
};
